import { useState, type ReactNode } from "react";
import { Dialog } from "../../../components/ui";
import { Icon } from "../../../components/Icon";
import { usePlattformSchalter } from "../../../components/Tarif";
import { money, moneyWhole } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import { MODUL_KEYS, TARIF_GRUPPEN, TARIF_STUFEN, isCreatorPlan, planName as katalogName, upgradeText } from "../../../model/tarif";
import type { AboData, PlanRow } from "./data";
import { ENTITLEMENT_LABELS, aufpreisKurz, aufpreisText, monatsCents, nettoPreis } from "./model";

/**
 * Tarife vergleichen in der App (K-373): je Kontoart die Karten mit den wichtigsten Unterschieden und je „Upgrade auf …“, darunter auf
 * Wunsch die ganze Tabelle. Dieselben Bausteine wie die Tarifwahl vor dem Abschluss (ZahlungsmethodeStart): „für wen“, die
 * Kennzeichen „Unsere Empfehlung“ und „Größter Umfang“ und die Tabelle „Tarife im Vergleich“.
 * K-375: Module, die der Betrieb noch nicht freigeschaltet hat (Instagram-Insights, WhatsApp, eigene Absenderdomain), stehen
 * als „in Vorbereitung“ da und zählen nicht als Grund für ein Upgrade.
 */

/** Wert einer Grenze für Anzeige und Vergleich: „25“, „unbegrenzt“, „25 GB“ */
export function grenzWert(key: string, v: number | undefined): string {
  if (v === undefined) return "–";
  if (v < 0) return t("unbegrenzt", "unlimited");
  return key === "storage_gb" ? `${v} GB` : String(v);
}

/**
 * Kennzeichen an der Karte: die Empfehlung und der größte Umfang. L (K-491): Gibt es aus dem Test eine Empfehlung
 * (trial_scope.recommended, der kleinste Tarif, der alles im Test Genutzte abdeckt), trägt dieser Tarif „Empfohlen für dich“;
 * sonst trägt Team „Unsere Empfehlung“.
 */
export function tarifMarke(key: string, empfohlen?: string | null): string | null {
  if (empfohlen) {
    if (key === empfohlen) return t("Empfohlen für dich", "Recommended for you");
    return key === "agentur" ? t("Größter Umfang", "Most complete") : key === "creator_pro" ? t("Für Profis", "For professionals") : null;
  }
  // Inhaber 21.10.2026: wie auf der Website „Empfohlen“
  if (key === "team") return t("Empfohlen", "Recommended");
  if (key === "creator_pro") return t("Für Profis", "For professionals");
  if (key === "agentur") return t("Größter Umfang", "Most complete");
  return null;
}
/** Für wen der Tarif gedacht ist (eine Zeile je Karte); unbekannte Tarife bleiben ohne Zeile. K-375: ohne Instagram-Kennzahlen. */
export function tarifFuerWen(key: string): string {
  if (key === "start") return t("Für dich allein: Kooperationen, Verträge und Rechnungen an einem Ort statt in Tabellen und Postfächern.", "For you alone: collaborations, contracts and invoices in one place instead of spreadsheets and inboxes.");
  if (key === "team") return t("Für Managements mit Team: Ihr teilt euch die Arbeit, zahlt Creator per Lauf aus und schickt Marken Angebote und Media-Kits per Link.", "For managements with a team: you share the work, pay creators in runs and send brands offers and media kits by link.");
  if (key === "agentur" || key === "agentur_alt") return t("Für Agenturen: großes Roster, der Kontoauszug-Import schlägt die passende Rechnung vor, und Mails gehen ohne CreatorOffice-Hinweis raus.", "For agencies: large roster, the bank statement import suggests the matching invoice, and emails go out without the CreatorOffice note.");
  if (key === "creator") return t("Für selbständige Creator ohne Agentur: dein eigenes Büro für Marken, Verträge und Rechnungen.", "For independent creators without an agency: your own office for brands, contracts and invoices.");
  if (key === "creator_pro") return t("Für Creator, die davon leben: Anfragen und Angebote, Media-Kits per Link und Export für die Buchhaltung.", "For creators who make a living from it: requests and offers, media kits by link and export for your accounting.");
  return "";
}

/** Grundleistungen auf der ersten Karte (ohne Vorgänger); im Creator-Konto ohne Gutschriften und ohne Creator-Portal (K-499) */
export function basisLeistungen(key: string): string[] {
  if (isCreatorPlan(key)) return [
    t("Kooperationen mit Monatsplan und Kalender", "Collaborations with monthly plan and calendar"),
    t("Verträge mit Baukasten und Unterschrift per Link", "Contracts with builder and signature by link"),
    t("Rechnungen an Marken und Zahlungseingänge", "Invoices to brands and incoming payments"),
  ];
  return [
    t("Kooperationen mit Monatsplan und Kalender", "Collaborations with monthly plan and calendar"),
    t("Verträge mit Baukasten und Unterschrift per Link", "Contracts with builder and signature by link"),
    t("Rechnungen, Gutschriften und Zahlungseingänge", "Invoices, credit notes and incoming payments"),
    t("Eigenes Portal für deine Creator", "A portal of their own for your creators"),
  ];
}

/** Freischaltung der Module, die der Betrieb erst noch einrichtet (platform_defaults) */
export interface ModulBereit { insights: boolean; whatsapp: boolean; absender: boolean }
export function useModulBereit(): ModulBereit {
  const insights = usePlattformSchalter("instagram_insights_ready");
  const whatsapp = usePlattformSchalter("whatsapp_sender_ready");
  const absender = usePlattformSchalter("custom_sender_domains_ready");
  return { insights: insights.data === true, whatsapp: whatsapp.data === true, absender: absender.data === true };
}
/** Modul noch nicht freigeschaltet (K-375)? */
export function inVorbereitung(key: string, bereit: ModulBereit): boolean {
  // Inhaber 21.10.2026: Die eigene Absenderdomain ist live und wird verkauft (Website: normale Leistung im Tarif Agentur); auf den
  // Tarifkarten nicht mehr „Demnächst“. Ob der Versand über die Domain schon läuft, steuert weiter custom_sender_domains_ready
  // (Vorlagen, Betrieb), nicht die Tarifkarte.
  return (key === "module_insights" && !bereit.insights) || (key === "module_whatsapp" && !bereit.whatsapp);
}
/** Bezeichnung eines Moduls auf einer Tarifkarte, mit „(in Vorbereitung)“, solange es noch nicht freigeschaltet ist */
export function modulAufKarte(key: string, bereit: ModulBereit): string {
  const name = ENTITLEMENT_LABELS[key] ?? key;
  return inVorbereitung(key, bereit) ? t(`${name} (in Vorbereitung)`, `${name} (in preparation)`) : name;
}

/** K-504: Module in Vorbereitung nicht in der Liste, sondern wie auf der Website als eigene Zeile „Demnächst: …“ unter der Liste */
export function baldText(keys: readonly string[], bereit: ModulBereit): string | null {
  const namen = keys.filter((k) => inVorbereitung(k, bereit)).map((k) => ENTITLEMENT_LABELS[k] ?? k);
  if (!namen.length) return null;
  const liste = namen.length === 1 ? namen[0]! : `${namen.slice(0, -1).join(", ")}${t(" und ", " and ")}${namen.at(-1)}`;
  return t(`Demnächst: ${liste}`, `Coming soon: ${liste}`);
}

/**
 * Karten der Tarife: je Karte die Grenzen und nur die Module, die gegenüber dem kleineren Tarif dazukommen. Noch nicht
 * freigeschaltete Module stehen am Ende der Liste (kein Werbeargument vor den echten Unterschieden).
 */
export function tarifKarten(data: Pick<AboData, "entitlements">, plans: readonly PlanRow[], bereit: ModulBereit) {
  const wert = (planKey: string, key: string): number | null => data.entitlements.find((e) => e.plan_key === planKey && e.key === key)?.value ?? null;
  const moduleVon = (planKey: string): string[] => MODUL_KEYS.filter((k) => (wert(planKey, k) ?? 0) >= 1);
  const zahl = (n: number | null, einheit: string, unbegrenzt: string): string | null => (n === null ? null : n < 0 ? unbegrenzt : `${n} ${einheit}`);
  return plans.map((p, i) => {
    const vorher = i > 0 ? plans[i - 1]! : null;
    const dazu = moduleVon(p.key).filter((k) => !vorher || !moduleVon(vorher.key).includes(k))
      .sort((a, b) => Number(inVorbereitung(a, bereit)) - Number(inVorbereitung(b, bereit)));
    return {
      plan: p, vorher, dazu,
      grenzen: [
        zahl(wert(p.key, "max_seats"), wert(p.key, "max_seats") === 1 ? t("Platz für dich", "seat for you") : t("Plätze fürs Team", "team seats"), t("Team ohne Grenze", "unlimited team seats")),
        zahl(wert(p.key, "max_talents"), wert(p.key, "max_talents") === 1 ? t("Creator", "creator") : t("Creator", "creators"), t("Creator ohne Grenze", "unlimited creators")),
        zahl(wert(p.key, "max_contracts_per_month"), t("Verträge im Monat", "contracts per month"), t("Verträge ohne Grenze", "unlimited contracts")),
        zahl(wert(p.key, "storage_gb"), t("GB Speicher", "GB storage"), t("Speicher ohne Grenze", "unlimited storage")),
      ].filter((x): x is string => !!x),
    };
  });
}

/** Rang eines Tarifs für „größer/kleiner“: Stufe Solo → Team → Agentur, sonst nach dem Monatspreis */
function rang(p: PlanRow): number {
  const i = (TARIF_STUFEN as readonly string[]).indexOf(p.key);
  return i >= 0 ? i * 1_000_000 + p.price_month_cents : p.price_month_cents;
}

/**
 * Fenster „Tarife vergleichen“ (K-373 b): drei Karten (Solo, Team, Agentur) mit den wichtigsten Unterschieden, je Karte
 * „Upgrade auf …“ bzw. „Zu … wechseln“; der Knopf schließt das Fenster und öffnet den Wechseldialog mit diesem Tarif.
 * Darunter aufklappbar die vollständige Tabelle. Kein Sprung auf die Website.
 */
export function TarifVergleichDialog({ open, onClose, data, current, onWahl, wechselMoeglich = true, empfohlen = null, laufend = false }: {
  open: boolean; onClose: () => void; data: AboData; current: string | null; onWahl: (plan: string) => void; wechselMoeglich?: boolean;
  /** Inhaber 16.10.2026: bei laufendem bezahlten Abo zeigen größere Tarife nur den Aufpreis im Monat */
  laufend?: boolean;
  /** L (K-491): Empfehlung aus dem Test */
  empfohlen?: string | null;
}) {
  const bereit = useModulBereit();
  const [tabelle, setTabelle] = useState(false);
  // Die Tarifliste ist schon auf die Kontoart gefiltert (data.ts): Agenturen Solo/Team/Agentur, Creator-Konten Creator/Creator Pro
  const plans = data.plans.filter((p) => p.active).sort((a, b) => rang(a) - rang(b));
  const aktuell = plans.find((p) => p.key === current) ?? null;
  const karten = tarifKarten(data, plans, bereit);
  return (
    <Dialog open={open} onClose={onClose} title={t("Tarife vergleichen", "Compare plans")} wide>
      <div className="stack tk-vergleich-dialog">
        <p className="muted">{t("Die wichtigsten Unterschiede auf einen Blick. Preise netto im Monat; bei jährlicher Abrechnung sind zwei Monate geschenkt.",
          "The main differences at a glance. Prices net per month; with annual billing you get two months free.")}</p>
        <div className={`tk-tarife tk-tarife-vergleich${karten.length === 1 ? " einzeln" : karten.length === 2 ? " zwei" : ""}`}>
          {karten.map(({ plan: p, vorher, dazu, grenzen }) => {
            const istAktuell = p.key === current;
            const groesser = !!aktuell && rang(p) > rang(aktuell);
            return (
              <div key={p.key} className={`tk-tarif tk-tarif-statisch${istAktuell ? " gewaehlt" : p.key === (empfohlen ?? "team") && (!aktuell || !!empfohlen) ? " empfohlen" : ""}`} role="group" aria-label={katalogName(p)}>
                <span className="tk-tarif-kopf">
                  <strong>{katalogName(p)}</strong>
                  {istAktuell ? <span className="pill info">{t("Dein Tarif", "Your plan")}</span> : tarifMarke(p.key, empfohlen) && <span className="pill">{tarifMarke(p.key, empfohlen)}</span>}
                </span>
                <span className="tk-tarif-fuer">{tarifFuerWen(p.key)}</span>
                {vorher && <span className="tk-tarif-dazu">{t(`Alles aus ${katalogName(vorher)}, dazu:`, `Everything in ${katalogName(vorher)}, plus:`)}</span>}
                <ul className="tk-tarif-liste">
                  {!vorher && basisLeistungen(p.key).map((x) => <li key={x}>{x}</li>)}
                  {dazu.filter((k) => !inVorbereitung(k, bereit)).map((k) => <li key={k}>{modulAufKarte(k, bereit)}</li>)}
                </ul>
                {baldText(dazu, bereit) && <span className="tk-tarif-bald">{baldText(dazu, bereit)}</span>}
                <span className="tk-tarif-umfang">{grenzen.join(" · ")}</span>
                <span className="tk-tarif-preis">
                  {laufend && groesser && aktuell ? (() => {
                    const iv = data.subscription?.billing_interval === "year" ? "year" : "month";
                    const diff = monatsCents(p, iv) - monatsCents(aktuell, iv);
                    return <><span className="num">{aufpreisKurz(diff)}</span><small className="tk-tarif-aufpreis">{aufpreisText(diff)}</small></>;
                  })() : <>
                  <span className="num">{nettoPreis(money(p.price_month_cents, "EUR"), "month")}</span>
                  {/* Inhaber 16.10.2026: keine Jahressummen in den Fenstern, auch die jährliche Abrechnung als Monatsbetrag */}
                  <small>{t(`oder ${money(Math.round(p.price_year_cents / 12), "EUR")} im Monat bei jährlicher Abrechnung`, `or ${money(Math.round(p.price_year_cents / 12), "EUR")} a month billed annually`)}</small>
                  </>}
                </span>
                {istAktuell
                  ? <span className="tk-tarif-wahl" aria-hidden="true">{t("Aktueller Tarif", "Current plan")}</span>
                  : wechselMoeglich && (
                    <button type="button" className={groesser || !aktuell ? "primary" : undefined} onClick={() => { onClose(); onWahl(p.key); }}>
                      {groesser ? <><Icon name="upgrade" />{upgradeText(katalogName(p))}</> : aktuell ? t(`Zu ${katalogName(p)} wechseln`, `Switch to ${katalogName(p)}`) : t(`${katalogName(p)} wählen`, `Choose ${katalogName(p)}`)}
                    </button>
                  )}
              </div>
            );
          })}
        </div>
        <div className="row">
          <button type="button" className="quiet" aria-expanded={tabelle} onClick={() => setTabelle((v) => !v)}>
            {tabelle ? t("Tabelle ausblenden", "Hide table") : t("Alle Funktionen im Vergleich", "Compare all features")}
          </button>
        </div>
        {tabelle && <Vergleich data={data} id="tarif-vergleich-fenster" onWahl={wechselMoeglich ? (k) => { onClose(); onWahl(k); } : undefined} />}
      </div>
    </Dialog>
  );
}

/** Kurze Zeilenbeschriftungen der Grenzen in der Vergleichstabelle; die Einheit steht im Wert (D-2) */
function kurzLabel(key: string): string {
  if (key === "max_talents") return t("Creator", "Creators");
  if (key === "max_seats") return t("Team-Plätze", "Team seats");
  if (key === "max_contracts_per_month") return t("Verträge/Monat", "Contracts/month");
  if (key === "storage_gb") return t("Speicher", "Storage");
  return ENTITLEMENT_LABELS[key] ?? key;
}
/** Betrag ohne „,00“ bei ganzen Euro („129 €“), sonst mit Cent („107,50 €“) */
function euro(cents: number): string {
  return cents % 100 === 0 ? moneyWhole(cents, "EUR") : money(cents, "EUR");
}

/**
 * Tabelle „Tarife im Vergleich“: EINE Komponente für Abo & Tarif (#tarif-vergleich), das Fenster „Tarife vergleichen“ und
 * „Alle Funktionen vergleichen“ im Zahlungsschritt (D-2, Inhaber 21.10.2026, Vorbild Zoho Books). Feste Spalten (erste Spalte
 * 40 %, Tarifspalten gleich breit), Kopf mit Tarifname, Monatspreis fett und klein dem Monatsbetrag bei jährlicher Abrechnung
 * (keine Jahressummen in Fenstern, Inhaber 16.10.2026), Kopf bleibt beim Scrollen stehen. Der aktuelle Tarif ist leicht
 * getönt mit „Dein Tarif“, die anderen tragen ein kleines „Wechseln“ (nur mit onWahl). Enthalten = grünes ✓, nicht
 * enthalten = graues –, beides ohne Text (Vorleser hören „enthalten“ bzw. „nicht enthalten“); Funktionen in Vorbereitung
 * als kleines „Demnächst“.
 */
export function Vergleich({ data, id = "tarif-vergleich", onWahl }: { data: AboData; id?: string; onWahl?: (plan: string) => void }) {
  const plans = data.plans.filter((p) => p.active);
  // Ehrlich (Befund 23): WhatsApp-Hinweise und Insights erst als Leistung, wenn sie freigeschaltet sind
  const bereit = useModulBereit();
  const aktuell = data.subscription?.plan_key ?? null;
  const zelle = (plan: string, key: string): ReactNode => {
    const v = data.entitlements.find((e) => e.plan_key === plan && e.key === key)?.value;
    if (v === undefined) return <span className="tk-tv-nein" aria-label={t("nicht enthalten", "not included")}>–</span>;
    if (key.startsWith("module_")) {
      if (v < 1) return <span className="tk-tv-nein" aria-label={t("nicht enthalten", "not included")}>–</span>;
      // gleiche Kennzeichnung wie auf der Website: noch nicht freigeschaltete Module nicht als enthalten zeigen
      if ((key === "module_whatsapp" && !bereit.whatsapp) || (key === "module_insights" && !bereit.insights)) return <small className="tk-tv-bald">{t("Demnächst", "Coming soon")}</small>;
      return <span className="tk-tv-ja" aria-label={t("enthalten", "included")}>✓</span>;
    }
    return <span className="num">{grenzWert(key, v)}</span>;
  };
  const spalte = (p: PlanRow) => (p.key === aktuell ? "tk-tv-aktuell" : undefined);
  return (
    <section className="stack tk-tv" id={id} aria-labelledby={`${id}-titel`}>
      <h3 id={`${id}-titel`}>{t("Tarife im Vergleich", "Plans compared")}</h3>
      <div className="tk-tv-rahmen">
        <table className="tk-tarif-vergleich">
          <caption className="sr-only">{t("Tarife im Vergleich", "Plans compared")}</caption>
          <colgroup>
            <col className="tk-tv-erste" />
            {plans.map((p) => <col key={p.key} className={spalte(p)} />)}
          </colgroup>
          <thead>
            <tr>
              <th scope="col"><span className="sr-only">{t("Umfang", "Scope")}</span></th>
              {plans.map((p) => (
                <th key={p.key} scope="col" className={spalte(p)}>
                  <span className="tk-tv-name">{katalogName(p)}</span>
                  <span className="tk-tv-preis num">{euro(p.price_month_cents)}{t("/Monat", "/month")}</span>
                  <small className="tk-tv-jahr">{t(`oder ${euro(Math.round(p.price_year_cents / 12))}/Monat jährlich`, `or ${euro(Math.round(p.price_year_cents / 12))}/month billed annually`)}</small>
                  {p.key === aktuell
                    ? <span className="pill info tk-tv-dein">{t("Dein Tarif", "Your plan")}</span>
                    : onWahl && <button type="button" className="tk-tv-wechseln" onClick={() => onWahl(p.key)} aria-label={t(`Zu ${katalogName(p)} wechseln`, `Switch to ${katalogName(p)}`)}>{t("Wechseln", "Switch")}</button>}
                </th>
              ))}
            </tr>
          </thead>
          {TARIF_GRUPPEN.map((g) => (
            <tbody key={g.key}>
              <tr className="tk-tarif-gruppe"><th scope="colgroup" colSpan={plans.length + 1}>{g.label}</th></tr>
              {g.keys.map((k) => (
                <tr key={k}>
                  <th scope="row">{kurzLabel(k)}</th>
                  {plans.map((p) => <td key={p.key} className={spalte(p)}>{zelle(p.key, k)}</td>)}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </section>
  );
}
